import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import PropertyCard from "@/components/PropertyCard";
import { publicApi } from "@/lib/api";

const AMENITIES = ["pool", "parking", "elevator", "sea_view", "exclusive"];

export default function Properties() {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("search") || "");

  const type = params.get("type") || "all";
  const location = params.get("location") || "all";
  const search = params.get("search") || "";

  useEffect(() => {
    setLoading(true);
    const q = { is_investment: false, is_rental: false, country: "Spain" };
    if (type !== "all") q.type = type;
    if (location !== "all") q.location = location;
    if (search) q.search = search;
    AMENITIES.forEach((a) => {
      if (params.get(a) === "1") q[a] = true;
    });
    publicApi
      .listProperties(q)
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [type, location, search, params.toString()]);

  const locations = useMemo(() => Array.from(new Set(list.map((p) => p.location))), [list]);

  function setFilter(k, v) {
    const next = new URLSearchParams(params);
    if (v === "all" || v === "" || v === null) next.delete(k);
    else next.set(k, v);
    setParams(next);
  }
  function toggleAmenity(a) {
    const next = new URLSearchParams(params);
    if (next.get(a) === "1") next.delete(a);
    else next.set(a, "1");
    setParams(next);
  }
  function submitSearch(e) {
    e.preventDefault();
    setFilter("search", searchInput.trim());
  }
  function reset() {
    setSearchInput("");
    setParams(new URLSearchParams());
  }

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="overline mb-3">{t("nav.properties")} · Mallorca</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-tight text-ink font-light tracking-tight">
          {t("properties.heading")}
        </h1>
        <p className="mt-6 text-lg text-ink max-w-2xl font-normal leading-relaxed">
          {t("properties.mallorca_only")}
        </p>

        <div className="mt-12 space-y-5 pb-6 border-b border-line">
          <form onSubmit={submitSearch} data-testid="properties-search-form" className="flex items-center gap-3 border border-line bg-white/60 focus-within:border-ink px-4 py-3 max-w-xl">
            <Search size={16} strokeWidth={1.6} className="text-muted" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("properties.search_placeholder")}
              data-testid="properties-search-input"
              className="flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-muted"
            />
            <button type="submit" data-testid="properties-search-submit" className="text-xs uppercase tracking-widest2 text-ink hover:text-accent">
              {t("properties.search_go")}
            </button>
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <span className="overline mr-2">{t("properties.filter_type")}:</span>
            {[
              ["all", t("properties.filter_all")],
              ["villa", t("properties.villa")],
              ["apartment", t("properties.apartment")],
              ["penthouse", t("properties.penthouse")],
              ["townhouse", t("properties.townhouse")],
              ["finca", t("properties.finca")],
              ["chalet", t("properties.chalet")],
              ["plot", t("properties.plot_land")],
              ["commercial", t("properties.commercial")],
              ["offmarket", t("properties.offmarket")],
            ].map(([val, label]) => (
              <button
                key={val}
                data-testid={`filter-type-${val}`}
                onClick={() => setFilter("type", val)}
                className={`text-sm px-3 py-1 border transition-colors ${
                  type === val ? "border-ink bg-ink text-bg" : "border-line text-ink hover:border-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {locations.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline mr-2">{t("properties.filter_location")}:</span>
              <button
                onClick={() => setFilter("location", "all")}
                data-testid="filter-loc-all"
                className={`text-sm px-3 py-1 border transition-colors ${
                  location === "all" ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"
                }`}
              >
                {t("properties.filter_all")}
              </button>
              {locations.map((loc) => (
                <button
                  key={loc}
                  data-testid={`filter-loc-${loc}`}
                  onClick={() => setFilter("location", loc)}
                  className={`text-sm px-3 py-1 border transition-colors ${
                    location === loc ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"
                  }`}
                >
                  {loc}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="overline mr-2">{t("properties.filter_amenity")}:</span>
            {AMENITIES.map((a) => {
              const on = params.get(a) === "1";
              return (
                <button
                  key={a}
                  onClick={() => toggleAmenity(a)}
                  data-testid={`filter-amenity-${a}`}
                  className={`text-sm px-3 py-1 border transition-colors ${
                    on ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"
                  }`}
                >
                  {t(`amenities.${a}`)}
                </button>
              );
            })}
            {(search || Array.from(params.keys()).some((k) => AMENITIES.includes(k))) && (
              <button
                onClick={reset}
                data-testid="filter-reset"
                className="ml-2 text-xs uppercase tracking-widest2 text-muted hover:text-ink"
              >
                {t("properties.reset")}
              </button>
            )}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-line" />
                  <div className="h-4 mt-4 bg-line w-1/3" />
                  <div className="h-6 mt-2 bg-line w-2/3" />
                </div>
              ))
            : list.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
        </div>

        {!loading && list.length === 0 && (
          <div data-testid="properties-empty" className="text-muted mt-16">
            {t("properties.no_results")}
          </div>
        )}
      </div>
    </div>
  );
}
